<script setup lang="ts">
import CommonBreadcrumb from '~/components/common/Breadcrumb.vue'
import { getRegulatorBlacklistList } from '~/api/regulator-blacklist'
import { getRegulatorCodeByEnShortName } from '~/api/regulator-list'

definePageMeta({
  name: 'disclosures-blacklist',
  layout: 'default',
})
const { getBreadcrumb } = useLsBreadcrumb()
const breadcrumbObj = ref<any>({})

const route = useRoute()
const { t, localePath } = useAppI18n()

/** 当前监管机构 slug，兼容 Nuxt 动态路由数组入参。 */
const regulatorSlug = computed(() => normalizeRouteValue(route.params.slug))

/** 通过当前路由 slug 解析监管机构 code。 */
const { data: regulatorCodeData } = await useAsyncData(
  () => `regulator-blacklist-code-${regulatorSlug.value}`,
  () => getRegulatorCodeByEnShortName(regulatorSlug.value),
  {
    watch: [regulatorSlug],
  },
)
const regulatorCode = computed(() => regulatorCodeData.value || '')

/** 当前监管机构的展示名称。 */

/** 获取黑名单列表数据 */
const { data: blacklistData } = await useAsyncData(
  () => `regulator-blacklist-${regulatorCode.value}`,
  () => {
    if (!regulatorCode.value) {
      return null
    }

    return getRegulatorBlacklistList({ regulatorCode: regulatorCode.value, size: 50 })
  },
  {
    watch: [regulatorCode],
  },
)

/** 将接口的多种交易所字段映射为 Figma 表格所需的稳定结构。 */
const blacklistRows = computed(() => blacklistData.value?.blacklistList || [])
const blacklistCount = computed(() => blacklistData.value?.blacklistCount || 0)

/** 页面面包屑，复用项目公共组件。 */
const breadcrumbItems = computed(() =>
  [{ label: t('82520'), route: '/' }, breadcrumbObj.value, { label: t('84640') }].filter(Boolean),
)
useSeoMeta({
  title: () => t('84884', [breadcrumbObj.value?.label]),
  ogTitle: () => t('84884', [breadcrumbObj.value?.label]),
  description: () => t('84880', [breadcrumbObj.value?.label]),
  ogDescription: () => t('84880', [breadcrumbObj.value?.label]),
})

/**
 * 标准化动态路由参数。
 * @param value 原始路由值
 * @returns 可用于展示的 slug
 */
function normalizeRouteValue(value: unknown) {
  if (Array.isArray(value)) {
    return String(value[0] || '').trim()
  }

  return String(value || '').trim()
}

/**
 * 获取交易所详情页的本地化链接。
 * @param exchangeSlug 交易所别名
 * @returns 详情页路径
 */
function getExchangeDetailLink(exchangeSlug?: string) {
  return localePath({
    name: 'exchange-detail',
    params: { slug: exchangeSlug || '' },
  })
}

const { overflowMap, checkOverflow } = useTextOverflow()
const { setBreadcrumb } = useLsBreadcrumb()

/** 缓存交易所详情页的列表来源面包屑。 */
const setExchangeDetailBreadcrumb = (broker: any) => {
  if (!broker.alias) return
  setBreadcrumb(broker.alias, {
    label: broker.brokerShortName,
    route: route.fullPath,
  })
}

onMounted(() => {
  breadcrumbObj.value = getBreadcrumb('disclosures-blacklist')
})
</script>

<template>
  <main class="min-h-screen bg-white pt-[70px] pb-[80px]">
    <section class="mx-auto w-[1200px] max-[1248px]:mx-[24px] max-[1248px]:w-auto">
      <!-- 公共头部由 default layout 提供；页面从面包屑开始对齐 Figma 内容区。 -->
      <CommonBreadcrumb :items="breadcrumbItems" />

      <h1 class="m-[0] mt-[12px] text-[24px] leading-[32px] font-[600] text-[#292a2e]">
        {{ t('84640') }}&nbsp; {{ blacklistCount }}
      </h1>

      <div class="mt-[24px] overflow-x-auto">
        <div class="min-w-[1000px]">
          <!-- 表头与列表使用一致列宽，匹配设计稿的五列数据结构。 -->
          <div
            class="grid h-[44px] grid-cols-[minmax(0,1fr)_minmax(0,1fr)_170px_110px_110px] items-center border-b border-[#112d73]"
          >
            <div class="px-[12px] text-[13px] leading-[20px] font-[400] text-[#7d818a]">{{ t('80337') }}</div>
            <div class="px-[12px] text-[13px] leading-[20px] font-[400] text-[#7d818a]">{{ t('84340') }}</div>
            <div class="px-[12px] text-[13px] leading-[20px] font-[400] text-[#7d818a]">{{ t('84341') }}</div>
            <div class="px-[12px] text-[13px] leading-[20px] font-[400] text-[#7d818a]">{{ t('82495') }}</div>
            <div class="px-[12px] text-center text-[13px] leading-[20px] font-[400] text-[#7d818a]">
              {{ t('84343') }}
            </div>
          </div>

          <div v-if="blacklistRows.length" class="divide-y divide-[rgba(11,18,40,0.14)]">
            <NuxtLink
              v-for="broker in blacklistRows"
              :key="broker.brokerCode"
              :to="getExchangeDetailLink(broker.alias)"
              target="_blank"
              class="grid h-[80px] cursor-pointer grid-cols-[minmax(0,1fr)_minmax(0,1fr)_170px_110px_110px] items-center text-inherit no-underline transition-colors duration-200 hover:bg-[#f8f8f8]"
              @click="setExchangeDetailBreadcrumb(broker)"
            >
              <div class="flex min-w-0 items-center gap-[12px] px-[12px]">
                <div
                  class="flex size-[44px] shrink-0 items-center justify-center overflow-hidden rounded-[4px] bg-[#f3f6ff]"
                >
                  <CommonImage
                    v-if="broker.brokerIcon"
                    class="size-full object-cover"
                    :src="broker.brokerIcon"
                    :alt="broker.brokerShortName"
                    width="44"
                    height="44"
                    object-fit="cover"
                  />
                </div>

                <div class="flex min-w-0 flex-col gap-[2px]">
                  <el-tooltip
                    :content="broker.brokerShortName"
                    placement="top"
                    popper-class="regulator-member-tooltip"
                    :show-after="0"
                    :offset="6"
                    :disabled="!overflowMap[broker.brokerCode || '']"
                  >
                    <p
                      class="m-[0] line-clamp-2 max-w-full text-[16px] leading-[24px] font-[500] break-words text-[#292a2e]"
                      @mouseenter="checkOverflow($event, broker.brokerCode || '')"
                    >
                      {{ broker.brokerShortName || '--' }}
                    </p>
                  </el-tooltip>
                  <div class="flex items-center gap-[8px] text-[12px] leading-[18px]">
                    <span v-if="broker.tags && broker.tags.length > 0" :style="{ color: broker.tags[0]?.textColor }">{{
                      broker.tags[0]?.text
                    }}</span>
                    <span
                      v-if="broker.tags && broker.tags.length > 1"
                      class="h-[14px] w-px bg-[rgba(11,18,40,0.14)]"
                    ></span>
                    <span v-if="broker.tags && broker.tags.length > 1" :style="{ color: broker.tags[1]?.textColor }">{{
                      broker.tags[1]?.text
                    }}</span>
                  </div>
                </div>
              </div>

              <div class="min-w-0 px-[12px]">
                <el-tooltip
                  v-if="broker?.businessTypes"
                  :content="broker.businessTypes"
                  placement="top"
                  popper-class="regulator-member-tooltip"
                  :show-after="0"
                  :offset="6"
                  :disabled="!overflowMap[broker.brokerCode + '_biz']"
                >
                  <p
                    class="m-[0] inline-block max-w-full truncate text-[14px] leading-[22px] font-[500] text-[#292a2e]"
                    @mouseenter="checkOverflow($event, broker.brokerCode + '_biz')"
                  >
                    {{ broker.businessTypes || '--' }}
                  </p>
                </el-tooltip>
                <p v-else class="m-[0] truncate text-[14px] leading-[22px] font-[500] text-[#292a2e]">--</p>
              </div>

              <div class="min-w-0 px-[12px]">
                <p class="m-[0] truncate text-[14px] leading-[22px] font-[500] text-[#292a2e]">
                  {{ broker.countryName || '--' }}
                </p>
              </div>

              <div class="flex items-center gap-[8px] px-[12px] text-[18px] leading-[26px] font-[500] text-[#292a2e]">
                <CommonImage
                  v-if="broker.score"
                  src="https://resources1.interface003.com/web30/2026/06/27/c39ab257-18e0-423e-8579-d81791e03605.png"
                  class="h-[20px] w-[20px] text-[#292a2e]"
                />
                <span class="font-wiki-Bold">{{ broker.score || '--' }}</span>
              </div>

              <div class="flex justify-center px-[12px]">
                <span
                  class="inline-flex h-[32px] w-[86px] items-center justify-center rounded-[20px] bg-[#112d73] text-[12px] leading-[18px] font-[500] text-white transition-colors duration-200 hover:bg-[#0d245c]"
                >
                  {{ t('84342') }}
                </span>
              </div>
            </NuxtLink>
          </div>

          <div v-else class="flex min-h-[200px] items-center justify-center text-[14px] text-[#7d818a]">
            {{ t('83714') }}
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<style lang="less">
.regulator-member-tooltip.el-popper {
  max-width: 560px;
  padding: 7px 12px !important;
  border: none !important;
  border-radius: 4px !important;
  background: #ffffff !important;
  box-shadow:
    0 0 1px 0 rgba(0, 0, 0, 0.2),
    0 8px 20px 0 rgba(0, 0, 0, 0.12) !important;
  color: #292a2e !important;
  font-size: 14px !important;
  font-weight: 400 !important;
  line-height: 22px !important;
  text-align: center;
  white-space: normal !important;
  word-break: break-all !important;

  .el-popper__arrow::before {
    border: none !important;
    background: #ffffff !important;
  }
}
</style>
